import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Popup from "../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import { applyLeave, getMyLeaves, getTeacherStats } from "../../redux/teacherRelated/teacherToolsHandle";
import { underToolControl } from "../../redux/teacherRelated/teacherToolsSlice";

const formatDate = (d) => new Date(d).toLocaleDateString("en-GB");

const LEAVE_TYPES = [
    "Annual Leave",
    "Sick Leave",
    "Maternity Leave",
    "Paternity Leave",
    "Compassionate Leave",
    "Study Leave",
    "Other",
];

const STATUS_CHIP = {
    Pending: "warning",
    Approved: "success",
    Rejected: "error",
};

function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function computeDays(startDate, endDate) {
    if (!startDate || !endDate) return 0;
    const start = startOfDay(startDate);
    const end = startOfDay(endDate);
    return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
}

function balanceColor(days) {
    const n = Number(days) ?? 0;
    if (n > 5) return "success.main";
    if (n >= 1) return "warning.main";
    return "error.main";
}

function truncateReason(text, max = 50) {
    const t = String(text || "").trim();
    if (!t) return "—";
    return t.length > max ? `${t.slice(0, max)}…` : t;
}

const TeacherLeave = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { leavesList, teacherStats, loading, statestatus, response, error } = useSelector((s) => s.teacherTools);

    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.schoolId;
    const annualLeaveAllocated = currentUser?.annualLeaveAllocated;
    const annualLeaveUsed = currentUser?.annualLeaveUsed ?? 0;

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const [leaveType, setLeaveType] = useState("Annual Leave");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reason, setReason] = useState("");
    const [coverTeacher, setCoverTeacher] = useState("");

    const leaves = useMemo(() => (Array.isArray(leavesList) ? leavesList : []), [leavesList]);

    const leaveBalance = teacherStats?.leaveBalance ?? 0;
    const durationDays = useMemo(() => computeDays(startDate, endDate), [startDate, endDate]);

    const reload = useCallback(() => {
        if (!teacherId) return;
        dispatch(getMyLeaves(teacherId));
        dispatch(getTeacherStats(teacherId));
    }, [dispatch, teacherId]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage("Completed successfully!");
            setShowPopup(true);
            reload();
            setDialogOpen(false);
            resetForm();
            dispatch(underToolControl());
        }
    }, [statestatus, dispatch, reload]);

    useEffect(() => {
        const msg = response || error;
        if (msg && statestatus !== "added") {
            setMessage(typeof msg === "string" ? msg : "Request failed");
            setShowPopup(true);
            dispatch(underToolControl());
        }
    }, [response, error, statestatus, dispatch]);

    const resetForm = () => {
        setLeaveType("Annual Leave");
        setStartDate("");
        setEndDate("");
        setReason("");
        setCoverTeacher("");
    };

    const openApplyDialog = () => {
        resetForm();
        setDialogOpen(true);
    };

    const toggleExpand = (id) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    const handleSubmit = () => {
        if (!teacherId || !schoolId) {
            setMessage("Missing teacher or school information");
            setShowPopup(true);
            return;
        }
        if (!leaveType || !startDate || !endDate || !String(reason).trim()) {
            setMessage("Leave type, dates and reason are required");
            setShowPopup(true);
            return;
        }
        if (durationDays < 1) {
            setMessage("End date must be on or after start date");
            setShowPopup(true);
            return;
        }

        dispatch(
            applyLeave({
                teacherId,
                schoolId,
                leaveType,
                startDate,
                endDate,
                reason: String(reason).trim(),
                coverTeacher: String(coverTeacher || "").trim(),
            })
        );
    };

    const showLowBalanceWarning =
        leaveType === "Annual Leave" &&
        teacherStats != null &&
        durationDays > 0 &&
        Number(leaveBalance) < durationDays;

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Typography variant="h5" fontWeight={700}>
                    Leave Applications
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openApplyDialog}>
                    Apply for Leave
                </Button>
            </Stack>

            {teacherStats != null && (
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: balanceColor(leaveBalance) }}>
                        Annual leave balance: {leaveBalance} days remaining
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Used: {annualLeaveUsed ?? 0} / Allocated: {annualLeaveAllocated ?? "—"}
                    </Typography>
                </Paper>
            )}

            {loading && !teacherStats && (
                <Skeleton variant="rounded" height={72} sx={{ mb: 3 }} />
            )}

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell width={48} />
                                <StyledTableCell>Type</StyledTableCell>
                                <StyledTableCell>Start date</StyledTableCell>
                                <StyledTableCell>End date</StyledTableCell>
                                <StyledTableCell>Days</StyledTableCell>
                                <StyledTableCell>Reason</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading && leaves.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8}>
                                        <Skeleton height={48} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : leaves.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No leave applications yet
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                leaves.map((row) => {
                                    const id = row.id || row._id;
                                    const status = row.status || "Pending";
                                    const isOpen = expandedId === id;
                                    return (
                                        <React.Fragment key={id}>
                                            <StyledTableRow hover>
                                                <StyledTableCell />
                                                <StyledTableCell>{row.leaveType}</StyledTableCell>
                                                <StyledTableCell>{formatDate(row.startDate)}</StyledTableCell>
                                                <StyledTableCell>{formatDate(row.endDate)}</StyledTableCell>
                                                <StyledTableCell>{row.numberOfDays}</StyledTableCell>
                                                <StyledTableCell>{truncateReason(row.reason)}</StyledTableCell>
                                                <StyledTableCell>
                                                    <Chip
                                                        label={status}
                                                        size="small"
                                                        color={STATUS_CHIP[status] || "default"}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <Button
                                                        size="small"
                                                        endIcon={isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                        onClick={() => toggleExpand(id)}
                                                    >
                                                        View
                                                    </Button>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                            <StyledTableRow>
                                                <StyledTableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                                                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                        <Box sx={{ py: 2, px: 1 }}>
                                                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                                                Full details
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                                <strong>Reason:</strong> {row.reason || "—"}
                                                            </Typography>
                                                            {row.coverTeacher ? (
                                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                                    <strong>Cover teacher:</strong> {row.coverTeacher}
                                                                </Typography>
                                                            ) : null}
                                                            {status === "Rejected" && row.rejectionNote ? (
                                                                <Alert severity="error" sx={{ mt: 1 }}>
                                                                    <strong>Rejection note:</strong> {row.rejectionNote}
                                                                </Alert>
                                                            ) : null}
                                                        </Box>
                                                    </Collapse>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Apply for leave</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Leave type</InputLabel>
                            <Select label="Leave type" value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
                                {LEAVE_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="date"
                                label="Start date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="date"
                                label="End date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Stack>

                        {startDate && endDate ? (
                            <Typography variant="body2" color="text.secondary">
                                Duration: <strong>{durationDays}</strong> day{durationDays === 1 ? "" : "s"}
                            </Typography>
                        ) : null}

                        {showLowBalanceWarning ? (
                            <Alert severity="warning">
                                You have {leaveBalance} days remaining. This application is for {durationDays} days.
                            </Alert>
                        ) : null}

                        <TextField
                            fullWidth
                            required
                            multiline
                            minRows={3}
                            size="small"
                            label="Reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Cover teacher"
                            helperText="Name of teacher covering your class"
                            value={coverTeacher}
                            onChange={(e) => setCoverTeacher(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                        Submit application
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </Container>
    );
};

export default TeacherLeave;
